import { useState, type ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUserShadowAiStats } from '@/hooks/api/useShadowAiTools';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { ShadowAiToolsTabs } from '@/components/shared/shadow-ai';

import { SummaryStats } from './components';
import { DataTablePersonalRunningNowContent } from './DataTableRunningNowTab';
import type { PersonalRunningNowMeta } from './DataTableRunningNowTab/api';
import { DataTablePersonalInstalledToolsContent } from './DataTableInstalledAiToolsTab';
import { DataTablePersonalBrowserToolsContent } from './DataTableBrowserAiToolsTab';

type PersonalTabValue = 'browser' | 'installed' | 'running';

// Tab order is kept identical to the org Tools page (Browser · Installed ·
// Running) so both account types read the same.
const VALID_PERSONAL_TABS: readonly PersonalTabValue[] = [
  'browser',
  'installed',
  'running',
];

/** Resolve the active tab from `?tab=`; unknown/absent falls back to `browser`. */
function parsePersonalTab(raw: string | null): PersonalTabValue {
  return VALID_PERSONAL_TABS.includes(raw as PersonalTabValue)
    ? (raw as PersonalTabValue)
    : 'browser';
}

export default function ShadowAITab(): ReactElement {
  const [runningMeta, setRunningMeta] = useState<PersonalRunningNowMeta | null>(
    null
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parsePersonalTab(searchParams.get('tab'));

  const handleTabChange = (value: string): void => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  const { flags } = useFeatureFlags([flagsmithFeatureFlags.SHADOW_AI]);
  const shadowAiEnabled = flags[flagsmithFeatureFlags.SHADOW_AI] ?? false;
  const { data: stats, isLoading: statsLoading } =
    useUserShadowAiStats(shadowAiEnabled);

  return (
    <div className="space-y-6">
      <SummaryStats
        runningCount={stats?.runningNow ?? null}
        installedCount={stats?.installedTools ?? null}
        browserCount={stats?.browserTools ?? null}
        isLoading={statsLoading}
      />

      <ShadowAiToolsTabs
        value={activeTab}
        onValueChange={handleTabChange}
        browser={<DataTablePersonalBrowserToolsContent />}
        installed={<DataTablePersonalInstalledToolsContent />}
        running={
          <DataTablePersonalRunningNowContent
            lastHeartbeat={runningMeta?.lastHeartbeat ?? null}
            isLoading={runningMeta === null}
            onMeta={setRunningMeta}
          />
        }
      />
    </div>
  );
}
